import { Box, Grid, FormControl, TextField, Button, InputLabel } from '@mui/material'
import { Cached, ExpandLess, ExpandMore, Search } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers'
import styled from '@emotion/styled'
import { initialFilters, TContractsSearchFilters } from '.'
import { Dispatch, SetStateAction, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useQueryState } from 'react-router-use-location-state'
import { isValid } from 'date-fns'
import { formattedTimeStandart } from 'hooks/date/formattedTime'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

const StyledButton = styled(Button)({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '4px',
  height: '48px',
  padding: '14px',
  textDecoration: 'none',
  transition: 'all 0.4s ease 0s',
  '& svg': {
    color: '#fff',
    fontSize: '20px',
  },
})

const StyledInputLabel = styled(InputLabel)({
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#050f21',
  marginBottom: '4px',
})

type Props = {
  setFilters: Dispatch<SetStateAction<TContractsSearchFilters>>
  defaultValues: TContractsSearchFilters
}

export const ConcludedContractsFilters = ({ setFilters, defaultValues }: Props) => {
  const [openMoreFilters, setOpenMoreFilters] = useState(false)
  const [_page, setPage] = useQueryState('page', 0)
  const { t } = useTranslation()

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues,
  })

  const resetFilters = () => {
    setFilters(initialFilters)
    reset(initialFilters)
    setValue('createdDateFrom', null)
    setValue('createdDateTo', null)
  }

  const onSubmit = (values: TContractsSearchFilters) => {
    setPage(0)
    setFilters(values)
  }

  return (
    <>
      <Box
        sx={{
          margin: '32px 0',
          position: 'relative',
        }}
      >
        <form id="contract-filter-form" onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            justifyContent="space-between"
            rowGap="10px"
            columnGap={{ xs: 0, md: '10px' }}
            sx={{
              padding: '20px',
              border: '1px solid #e1e4e8',
              borderRadius: openMoreFilters ? '4px 4px 0 0' : '4px',
            }}
          >
            <Grid
              item
              xs={9}
              md={'auto'}
              sx={{
                flexGrow: '1 !important',
              }}
            >
              <TextField
                id="search"
                placeholder={t('tender-organization-name')}
                variant="outlined"
                fullWidth
                sx={{
                  maxHeight: '48px',
                  '& .MuiInputBase-input': {},
                  '& fieldset': {},
                }}
                {...register('contractorOrganizationName')}
              />
            </Grid>
            <Grid item xs={12} md={'auto'} order={{ xs: 1, md: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  border: '1px solid rgb(3, 34, 76)',
                  borderRadius: '4px',
                  padding: '14px 16px',
                  display: 'inline-flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: '.4s',
                  fontSize: '14px',
                  height: '48px',
                  fontWeight: 500,
                  color: 'rgb(3, 34, 76)',
                }}
                onClick={() => setOpenMoreFilters(!openMoreFilters)}
              >
                {t('search-more')}
                <ExpandMore />
              </Box>
            </Grid>
            <Grid item xs={3} md={'auto'}>
              <Box display="flex" justifyContent="flex-end" gap="10px">
                <StyledButton
                  sx={{
                    background: 'rgb(122, 127, 126)',
                    '&:hover': {
                      background: 'rgb(135, 141, 140)',
                    },
                  }}
                  type="submit"
                  form="contract-filter-form"
                  onClick={handleSubmit(onSubmit)}
                >
                  <Search />
                </StyledButton>
                <StyledButton
                  sx={{
                    background: 'rgb(3, 34, 76)',
                    color: '#fff',
                    '&:hover': {
                      background: 'rgba(3, 34, 76, 0.6)',
                    },
                  }}
                  onClick={resetFilters}
                >
                  <Cached />
                </StyledButton>
              </Box>
            </Grid>
          </Grid>
          {openMoreFilters && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                width: '100%',
                padding: '20px',
                border: '1px solid #e1e4e8',
                borderTop: 'none',
                zIndex: '99',
                background: '#fff',
              }}
            >
              <Grid
                container
                columnSpacing="20px"
                rowSpacing="25px"
                sx={{ marginBottom: '20px' }}
              >
                <Grid item xs={12} md={6}>
                  <StyledInputLabel id="competetion-number">
                    {t('createdDate')}
                  </StyledInputLabel>
                  <Box display="flex" gap="10px">
                    <FormControl sx={{ width: '50%' }}>
                      <Controller
                        name="createdDateFrom"
                        control={control}
                        render={({
                          field: { ref, onBlur, name, ...field },
                          fieldState,
                        }) => (
                          <DatePicker
                            {...field}
                            inputRef={ref}
                            value={field.value ? field.value : null}
                            onChange={(date) => {
                              if (
                                date !== null &&
                                isValid(new Date(formattedTimeStandart(date)))
                              ) {
                                const result = dayjs(date).toISOString()
                                field.onChange(result)
                              }
                            }}
                            slotProps={{
                              textField: {
                                placeholder: t('from'),
                                onBlur,
                                name,
                                error: !!fieldState?.error,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <FormControl sx={{ width: '50%' }}>
                      <Controller
                        name="createdDateTo"
                        control={control}
                        render={({
                          field: { ref, onBlur, name, ...field },
                          fieldState,
                        }) => (
                          <DatePicker
                            {...field}
                            inputRef={ref}
                            value={field.value ? field.value : null}
                            onChange={(date) => {
                              if (
                                date !== null &&
                                isValid(new Date(formattedTimeStandart(date)))
                              ) {
                                const result = dayjs(date).toISOString()
                                field.onChange(result)
                              }
                            }}
                            slotProps={{
                              textField: {
                                placeholder: t('to'),
                                onBlur,
                                name,
                                error: !!fieldState?.error,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledInputLabel id="competetion-number">
                    {t('contest-name')}
                  </StyledInputLabel>
                  <FormControl fullWidth>
                    <TextField
                      id="competetion-number"
                      InputLabelProps={{ shrink: false }}
                      placeholder={t('contest-name')}
                      variant="outlined"
                      fullWidth
                      sx={{
                        maxHeight: '48px',
                      }}
                      {...register('lotName')}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                rowGap="10px"
                sx={{
                  justifyContent: { xs: 'center', sm: 'space-between' },
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="ceter"
                  color="primary.main"
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => setOpenMoreFilters(false)}
                >
                  {t('close')} <ExpandLess sx={{ marginLeft: '5px' }} />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="outlined"
                    sx={{
                      marginRight: { xs: '15px' },
                      marginBottom: { xs: '15px', sm: 0 },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                    onClick={resetFilters}
                  >
                    {t('reset-search')}
                  </Button>
                  <Button
                    type="submit"
                    form="contract-filter-form"
                    onClick={handleSubmit(onSubmit)}
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    {t('show')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </form>
      </Box>
    </>
  )
}
